'use client';

import { type RefObject, useEffect, useRef } from 'react';
import { Chip } from '../../../../Chip/Chip';
import { useFormattedDateLabel } from '../hooks/useFormattedDateLabel';
import { getMonthFromKey, monthKeyToDate } from '../transactionDateHelpers';
import type { MonthKey } from '../types';

type MonthChipProps = {
  month: MonthKey;
  namespace: string;
  isChecked: boolean;
  onSelect: (monthKey: MonthKey) => void;
  containerRef: RefObject<HTMLDivElement | null>;
};

function scrollChipIntoView(container: HTMLElement, chip: HTMLElement) {
  const chipLeft = chip.offsetLeft;
  const chipRight = chipLeft + chip.offsetWidth;
  const { scrollLeft, clientWidth } = container;
  if (chipLeft < scrollLeft) {
    container.scrollTo({ left: chipLeft, behavior: 'smooth' });
  } else if (chipRight > scrollLeft + clientWidth) {
    container.scrollTo({ left: chipRight - clientWidth, behavior: 'smooth' });
  }
}

function MonthChip({ month, namespace, isChecked, onSelect, containerRef }: MonthChipProps) {
  const label = useFormattedDateLabel(namespace, monthKeyToDate(month), 'month_label');
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isChecked && containerRef.current && spanRef.current) {
      scrollChipIntoView(containerRef.current, spanRef.current);
    }
  }, [isChecked, containerRef]);

  return (
    <span ref={spanRef}>
      <Chip
        label={label}
        value={month}
        className="whitespace-nowrap"
        isChecked={isChecked}
        onChange={({ value, checked }) => {
          if (checked) onSelect(value as MonthKey);
        }}
        data-track-name={label}
        data-track-value={getMonthFromKey(month)}
      />
    </span>
  );
}

export { MonthChip };
